export type StorageArea = "local" | "session";

export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface StorageCodec<T> {
  parse: (value: string) => T;
  serialize: (value: T) => string;
}

export interface StorageEntry<T> {
  readonly area: StorageArea;
  readonly key: string;
  readonly codec: StorageCodec<T>;
}

export interface BrowserStorageAreas {
  local: StorageLike | null;
  session: StorageLike | null;
}

export const stringStorageCodec: StorageCodec<string> = {
  parse: (value) => value,
  serialize: (value) => value,
};

export const jsonStorageCodec = <T>(isValid?: (value: unknown) => value is T): StorageCodec<T> => ({
  parse: (value) => {
    const parsed: unknown = JSON.parse(value);
    if (isValid && !isValid(parsed)) {
      throw new Error("Stored JSON did not match the expected shape");
    }
    return parsed as T;
  },
  serialize: (value) => {
    const serialized = JSON.stringify(value);
    if (serialized === undefined) {
      throw new Error("Value cannot be represented as JSON");
    }
    return serialized;
  },
});

export const defineStorageEntry = <T>(entry: StorageEntry<T>): StorageEntry<T> => Object.freeze(entry);

const resolveWindowStorage = (area: StorageArea): StorageLike | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return area === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

export const createBrowserStorage = (areas?: Partial<BrowserStorageAreas>) => {
  const resolveStorage = (area: StorageArea): StorageLike | null => {
    if (areas) {
      return areas[area] ?? null;
    }
    return resolveWindowStorage(area);
  };

  return {
    get<T>(entry: StorageEntry<T>): T | null {
      try {
        const storedValue = resolveStorage(entry.area)?.getItem(entry.key);
        return storedValue == null ? null : entry.codec.parse(storedValue);
      } catch {
        return null;
      }
    },

    set<T>(entry: StorageEntry<T>, value: T): boolean {
      try {
        const storage = resolveStorage(entry.area);
        if (!storage) {
          return false;
        }
        storage.setItem(entry.key, entry.codec.serialize(value));
        return true;
      } catch {
        return false;
      }
    },

    remove<T>(entry: StorageEntry<T>): boolean {
      try {
        const storage = resolveStorage(entry.area);
        if (!storage) {
          return false;
        }
        storage.removeItem(entry.key);
        return true;
      } catch {
        return false;
      }
    },
  };
};

/**
 * The single low-level gateway for portal-owned browser storage.
 *
 * New entries must be defined by their owning feature with `defineStorageEntry`
 * and consumed through this object. Portal-owned session storage is migrated in
 * this change; unrelated direct localStorage access is the next migration target.
 * OIDC's `oidc.user:*` entry remains library-owned and is intentionally exempt.
 */
export const browserStorage = createBrowserStorage();
